// Per-player progress kept in localStorage. Storage can be unavailable (private mode, blocked
// site data), so every access is guarded and the game works without it.

import { sanitizeGarage, type Garage } from '../sim/upgrades';
import { sanitizeProgress, type Progress } from '../sim/missions';
import { sanitizeGhost, type GhostRecord } from '../sim/ghost';

const key = (envId: string) => `mbg-climb:best:${envId}`;

export function loadBest(envId: string): number {
  try {
    const value = Number(window.localStorage.getItem(key(envId)));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

export function saveBest(envId: string, distance: number): void {
  try {
    window.localStorage.setItem(key(envId), String(Math.floor(distance)));
  } catch {
    // Best distance simply won't persist this session.
  }
}

const GARAGE_KEY = 'mbg-climb:garage:v1';

/** Wallet and upgrade levels. Invalid or missing data falls back to an empty garage. */
export function loadGarage(): Garage {
  try {
    const raw = window.localStorage.getItem(GARAGE_KEY);
    return sanitizeGarage(raw ? JSON.parse(raw) : null);
  } catch {
    return sanitizeGarage(null);
  }
}

export function saveGarage(garage: Garage): void {
  try {
    window.localStorage.setItem(GARAGE_KEY, JSON.stringify(garage));
  } catch {
    // The garage lasts for this session only.
  }
}

const ENV_KEY = 'mbg-climb:env';

export function loadSelectedEnv(): string | null {
  try {
    return window.localStorage.getItem(ENV_KEY);
  } catch {
    return null;
  }
}

export function saveSelectedEnv(id: string): void {
  try {
    window.localStorage.setItem(ENV_KEY, id);
  } catch {
    // The choice lasts for this session only.
  }
}

const PROGRESS_KEY = 'mbg-climb:missions:v1';

export function loadProgress(day: string): Progress {
  try {
    const raw = window.localStorage.getItem(PROGRESS_KEY);
    return sanitizeProgress(raw ? JSON.parse(raw) : null, day);
  } catch {
    return sanitizeProgress(null, day);
  }
}

export function saveProgress(progress: Progress): void {
  try {
    window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch {
    // Progress lasts for this session only.
  }
}

const ghostKey = (envId: string) => `mbg-climb:ghost:${envId}`;

/** The saved best-run ghost for an environment, if it still replays under this build. */
export function loadGhost(envId: string): GhostRecord | null {
  try {
    const raw = window.localStorage.getItem(ghostKey(envId));
    const ghost = sanitizeGhost(raw ? JSON.parse(raw) : null);
    return ghost && ghost.envId === envId ? ghost : null;
  } catch {
    return null;
  }
}

export function saveGhost(ghost: GhostRecord): void {
  try {
    window.localStorage.setItem(ghostKey(ghost.envId), JSON.stringify(ghost));
  } catch {
    // Too big or storage blocked: the ghost lasts for this session only.
  }
}

export function deleteGhost(envId: string): void {
  try {
    window.localStorage.removeItem(ghostKey(envId));
  } catch {
    // Nothing to remove.
  }
}

export function loadGhostEnabled(): boolean {
  try {
    return window.localStorage.getItem('mbg-climb:ghost-on') !== '0';
  } catch {
    return true;
  }
}

export function saveGhostEnabled(on: boolean): void {
  try {
    window.localStorage.setItem('mbg-climb:ghost-on', on ? '1' : '0');
  } catch {
    // Lasts for this session only.
  }
}
